import { Box, styled, Typography } from "~/common/mui";
import { useTranslation } from "react-i18next";
import CircleSpinner from "~/common/components/CircleSpinner/CircleSpinner";
import { type ProductData } from "~/domains/product/types/ProductData";
import { formatCurrency } from "~/common/utils/currencyFormatter";
import { userIsCustomer } from "~/domains/user/utils/roles";
import CenterContents from "~/common/components/CenterContents";
import { text } from "~/utils/PriceUtils";
import { SaleTag } from "~/domains/salesCampaign/components/SaleTag/SaleTag";
import { Product } from "~/domains/product/Product";
import { getTotalPrice } from "~/domains/product/utils/productDescriptorUtils";
import type { NetPriceData, TotalPriceData, WithSurcharge } from "~/domains/pricing/types/PriceData";
import { SummaryLabel } from "./SummaryLabel";
import type { AddToCartResponseItem } from "~/features/cart/cart/api/AddToCart";
import { getAddToCartResponsePrice } from "~/features/cart/cart/utils/addToCartResponseItems";

const ContentGrid = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateRows: "auto auto auto",
  gridTemplateColumns: "max-content auto",
  gridTemplateAreas: `
    "image productInfo"
    "spacer spacer"
    ". productValues"
  `,
  rowGap: theme.spacing(2),
  columnGap: theme.spacing(3),
  [theme.breakpoints.up("tablet")]: {
    gridTemplateRows: "auto",
    gridTemplateColumns: "1fr 2fr 2fr 1fr",
    gridTemplateAreas: `"image productInfo productValues ."`,
  },
  [theme.breakpoints.up("desktop-s")]: {
    gridTemplateRows: "auto auto",
    gridTemplateColumns: "max-content auto",
    gridTemplateAreas: `
      "image productInfo"
      ". productValues"
    `,
  },
}));

const Spacer = styled(Box)(({ theme }) => ({
  gridArea: "spacer",
  width: "100%",
  borderTop: `1px solid`,
  borderColor: theme.palette.divider,
}));

const ImageContainer = styled(Box)({
  position: "relative",
  display: "flex",
  justifyContent: "end",
  gridArea: "image",
  "& img": {
    width: 64,
    height: 64,
    objectFit: "contain",
  },
});

const ProductInfo = styled(Box)(({ theme }) => ({
  width: "100%",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
  gridArea: "productInfo",
  "& > div": {
    alignItems: "center",
  },
}));

const ProductValues = styled(Box)(({ theme }) => ({
  width: "100%",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
  paddingRight: theme.spacing(1),
  gridArea: "productValues",
  [theme.breakpoints.up("tablet")]: {
    paddingRight: 0,
  },
}));

const SalesPriceContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(0.5),
}));

const BasePrice = styled(Typography)({
  textDecoration: "line-through",
});

const NewPrice = styled(Typography)({
  fontWeight: 700,
});

export interface SingleItemViewProps {
  productInfo: ProductData;
  quantity: number;
  isBusiness: boolean;
  netPrice: number | null;
  basePrice: number | null;
  hasSales: boolean;
  totalPrice: TotalPriceData & WithSurcharge;
  priceUnit: number;
  tariffSurcharge: boolean;
}

export const SingleItemView = ({
  productInfo,
  quantity,
  isBusiness,
  netPrice,
  basePrice,
  hasSales,
  totalPrice,
  priceUnit,
  tariffSurcharge,
}: SingleItemViewProps) => {
  const { t } = useTranslation();

  return (
    <ContentGrid>
      <Spacer />
      <ImageContainer>
        <Product.PrimaryImageV2 product={productInfo} size={64} />
        {hasSales && <SaleTag sx={{ position: "absolute", top: 0, left: 0 }} size={24} />}
      </ImageContainer>

      <ProductInfo>
        <Product.DesignationContainer>
          <Product.PreferredIndicator product={productInfo} />
          <Product.DesignationV2 product={productInfo} variant="titleMedium" sx={{ fontWeight: "bold" }} />
        </Product.DesignationContainer>
        <Product.ShortDescriptionV2 product={productInfo} />
      </ProductInfo>
      <ProductValues>
        <SummaryLabel label={t("feedback.option.quantity")} value={quantity.toString()} />
        {isBusiness && (
          <>
            <SummaryLabel
              label={t("common.product.netPricePerPiece", { count: priceUnit })}
              value={
                netPrice ? (
                  basePrice ? (
                    <SalesPriceContainer>
                      <BasePrice>{formatCurrency(basePrice)}</BasePrice>
                      <NewPrice>{formatCurrency(netPrice)}</NewPrice>
                    </SalesPriceContainer>
                  ) : (
                    <Typography component="span" sx={hasSales ? { fontWeight: 700 } : undefined}>
                      {formatCurrency(netPrice)}
                    </Typography>
                  )
                ) : (
                  "-"
                )
              }
            />
            <SummaryLabel className="f-bold" label={t("wishlistPage.total")} value={text(totalPrice)} />
            {tariffSurcharge && <Typography variant="labelSmall">{t("price.tariff.surcharge.applicable")}</Typography>}
          </>
        )}
      </ProductValues>
    </ContentGrid>
  );
};

const AddToCartResultSingleItem = ({
  productInfo,
  responseItem,
}: {
  productInfo?: ProductData;
  responseItem?: AddToCartResponseItem;
}) => {
  const isBusiness = userIsCustomer();
  const resultPrice = responseItem ? getAddToCartResponsePrice(responseItem) : undefined;
  const netPrice = resultPrice?.value ?? null;
  const priceUnit = responseItem?.priceUnit ?? resultPrice?.unit ?? 1;

  if (!productInfo) {
    return (
      <CenterContents>
        <CircleSpinner />
      </CenterContents>
    );
  }

  let basePrice: number | null = null;

  const hasSales = Boolean(responseItem?.specialPrice || responseItem?.actualSalePrice);

  if (responseItem?.specialPrice && responseItem.actualNetPrice) {
    basePrice = responseItem.actualNetPrice.value;
  }

  const netPriceData: NetPriceData = resultPrice ?? {
    value: netPrice ?? 0,
    unit: priceUnit,
    baseValue: netPrice ?? 0,
    currencyIso: window.SCHA.config.currencyIso,
    surcharge: 0,
  };

  const quantity = responseItem?.quantity ?? 0;
  const totalPrice = responseItem?.totalPrice
    ? {
        value: responseItem.totalPrice.value,
        currencyIso: responseItem.totalPrice.currencyIso,
        baseValue: responseItem.totalPrice.value,
        surcharge: 0,
      }
    : getTotalPrice(netPriceData, quantity);

  return (
    <SingleItemView
      productInfo={productInfo}
      quantity={quantity}
      isBusiness={isBusiness}
      netPrice={netPrice}
      basePrice={basePrice}
      hasSales={hasSales}
      totalPrice={totalPrice}
      priceUnit={priceUnit}
      tariffSurcharge={false}
    />
  );
};

export default AddToCartResultSingleItem;
